// Utilidades 3D de las pantallas y luces: brillo por elemento con una sola malla (paleta emisiva) y halos
// en una sola llamada de dibujo, formas de los dígitos de 7 segmentos y conectores de pines.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { M, mat, glow } from '../kit.js';
import { PITCH } from '../../parts/registry.js';

// Emisión (lineal) del canal más fuerte de un LED a brillo pleno. Con el mapeo de tonos ACES un color
// saturado muy intenso se vuelve naranja y luego blanco: con ~1 el rojo de un display se ve rojo.
// Así tampoco se pasa el umbral del bloom (2.4) y las matrices grandes no lavan la escena.
export const LED_PEAK = 1.1;

/** Brillo percibido (0–1) de un LED con ciclo de trabajo d (0–1). */
export function perceived(d) {
  return d > 0 ? Math.pow(d, 0.45) : 0;
}

/** Emisión HDR de un LED de color `c` (THREE.Color lineal, componente máxima ~1) con brillo k (0–1). */
export function ledEmission(c, k, out, peak = LED_PEAK) {
  const s = Math.max(0, Math.min(1, k)) * peak;
  out[0] = c.r * s;
  out[1] = c.g * s;
  out[2] = c.b * s;
  return out;
}

/**
 * Muchos LEDs (segmentos, puntos de una matriz, NeoPixels) en una sola malla: cada elemento apunta con
 * sus UV a un texel de dos texturas de 1 × n: la emisión (en coma flotante) y el tinte del difusor
 * (blanco apagado, del color del LED encendido). Así se dibuja todo en una sola llamada.
 */
export class Palette {
  constructor(count, { color = '#e8e4da', roughness = 0.45, peak = LED_PEAK } = {}) {
    this.count = count;
    this.peak = peak;
    this.data = new Float32Array(count * 4);
    this.tex = paletteTexture(this.data, count, THREE.FloatType);
    this.tint = new Uint8Array(count * 4).fill(255);
    this.tintTex = paletteTexture(this.tint, count, THREE.UnsignedByteType);
    this.tintTex.colorSpace = THREE.SRGBColorSpace;
    this.material = new THREE.MeshStandardMaterial({
      color, map: this.tintTex, roughness, metalness: 0, emissive: '#ffffff', emissiveMap: this.tex, emissiveIntensity: 1,
    });
    this.geos = [];
    this.tmp = [0, 0, 0];
    this.chroma = new THREE.Color();
    this.srgb = new THREE.Color();
  }
  /** Agrega la geometría del elemento i (ya colocada) apuntando a su texel. */
  add(geo, i) {
    const g = geo.index ? geo.toNonIndexed() : geo.clone();
    const n = g.attributes.position.count;
    const uv = new Float32Array(n * 2);
    for (let k = 0; k < n; k++) {
      uv[k * 2] = (i + 0.5) / this.count;
      uv[k * 2 + 1] = 0.5;
    }
    g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    if (!g.attributes.normal) g.computeVertexNormals();
    for (const k of Object.keys(g.attributes)) if (!['position', 'normal', 'uv'].includes(k)) g.deleteAttribute(k);
    this.geos.push(g);
  }
  mesh() {
    const m = new THREE.Mesh(mergeGeometries(this.geos, false), this.material);
    m.receiveShadow = true;
    this.geos = [];
    return m;
  }
  /** Elemento i encendido con el color `color` (THREE.Color lineal) y brillo k (0–1). */
  set(i, color, k) {
    ledEmission(color, k, this.tmp, this.peak);
    const d = this.data;
    d[i * 4] = this.tmp[0];
    d[i * 4 + 1] = this.tmp[1];
    d[i * 4 + 2] = this.tmp[2];
    d[i * 4 + 3] = 1;
    // Encendido, el difusor toma el color del LED y refleja menos luz: si no, el blanco de la cara
    // iluminada por la escena lava el color.
    const t = Math.min(1, Math.max(0, k) * 2);
    this.srgb.copy(color).convertLinearToSRGB();
    const tt = this.tint;
    tt[i * 4] = Math.round(255 * (1 - t) + this.srgb.r * 110 * t);
    tt[i * 4 + 1] = Math.round(255 * (1 - t) + this.srgb.g * 110 * t);
    tt[i * 4 + 2] = Math.round(255 * (1 - t) + this.srgb.b * 110 * t);
  }
  /** LED RGB (valores 0–255 = ciclo de trabajo del PWM de cada color). */
  setRGB(i, r, g, b) {
    const m = Math.max(r, g, b);
    if (m <= 0) {
      this.set(i, this.chroma, 0);
      return;
    }
    // La mezcla de colores es lineal en el ciclo de trabajo; el brillo percibido no (un LED al 25 %
    // se ve a media luz), así que la intensidad pasa por una curva tipo gamma.
    this.chroma.setRGB(r / m, g / m, b / m);
    this.set(i, this.chroma, perceived(m / 255));
  }
  clear() {
    this.data.fill(0);
    this.tint.fill(255);
    this.commit();
  }
  commit() {
    this.tex.needsUpdate = true;
    this.tintTex.needsUpdate = true;
  }
  dispose() {
    this.tex.dispose();
    this.tintTex.dispose();
    this.material.dispose();
  }
}

/**
 * Halos de luz para muchos LEDs en una sola llamada de dibujo: puntos con la textura radial del kit,
 * mezcla aditiva y color por punto (negro = invisible). Funcionan con cualquier color, aunque no pase
 * el umbral del bloom.
 */
export class Halos {
  constructor(positions, size) {
    this.count = positions.length;
    const pos = new Float32Array(this.count * 3);
    positions.forEach((p, i) => pos.set(p, i * 3));
    this.colors = new Float32Array(this.count * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.colorAttr = new THREE.BufferAttribute(this.colors, 3);
    geo.setAttribute('color', this.colorAttr);
    const map = glow('#ffffff').material.map; // textura radial compartida del kit
    this.material = new THREE.PointsMaterial({
      size, map, vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true,
    });
    this.points = new THREE.Points(geo, this.material);
    this.points.userData.noPick = true;
    this.points.renderOrder = 10;
  }
  /** Halo i con el color `c` (THREE.Color lineal) e intensidad k (0–1). */
  set(i, c, k) {
    const f = Math.max(0, Math.min(1, k));
    this.colors[i * 3] = c.r * f;
    this.colors[i * 3 + 1] = c.g * f;
    this.colors[i * 3 + 2] = c.b * f;
  }
  clear() {
    this.colors.fill(0);
    this.commit();
  }
  commit() {
    this.colorAttr.needsUpdate = true;
  }
  dispose() {
    this.points.geometry.dispose();
    this.material.dispose();
  }
}

function paletteTexture(data, count, type) {
  const t = new THREE.DataTexture(data, count, 1, THREE.RGBAFormat, type);
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

/** Color lineal (para emisión) a partir de un color CSS. */
export function linear(hex) {
  return new THREE.Color(hex);
}

// Segmentos ------------------------------------------------------------------------------------------

/**
 * Polígonos de un dígito de 7 segmentos (A–G y DP) en mm, centrado en (0, 0), con inclinación.
 * h: alto del dígito, w: ancho, t: grosor del segmento, gap: separación entre segmentos.
 */
export function digitShapes({ h, w, t, gap = 0.25, slant = 0.17, dp = true, dpR = null }) {
  const hw = w / 2;
  const hh = h / 2;
  const ht = t / 2;
  const sk = (x, y) => new THREE.Vector2(x + y * slant, y);
  const horiz = (cy) => {
    const x0 = -hw + gap;
    const x1 = hw - gap;
    return [sk(x0, cy), sk(x0 + ht, cy + ht), sk(x1 - ht, cy + ht), sk(x1, cy), sk(x1 - ht, cy - ht), sk(x0 + ht, cy - ht)];
  };
  const vert = (cx, y0, y1) => {
    const a = y0 + gap;
    const b = y1 - gap;
    return [sk(cx, a), sk(cx + ht, a + ht), sk(cx + ht, b - ht), sk(cx, b), sk(cx - ht, b - ht), sk(cx - ht, a + ht)];
  };
  const shapes = [
    horiz(hh), // A
    vert(hw, 0, hh), // B
    vert(hw, -hh, 0), // C
    horiz(-hh), // D
    vert(-hw, -hh, 0), // E
    vert(-hw, 0, hh), // F
    horiz(0), // G
  ].map((pts) => new THREE.Shape(pts));
  if (dp) {
    const r = dpR || t * 0.62;
    const c = sk(hw + t * 0.9 + r, -hh + r * 0.15);
    const s = new THREE.Shape();
    s.absarc(c.x, c.y, r, 0, Math.PI * 2, false);
    shapes.push(s);
  }
  return shapes;
}

/** Forma 2D (x, y arriba) → geometría plana sobre el plano XZ a la altura y. */
export function flatShape(shape, x, y, z, segments = 12) {
  const g = new THREE.ShapeGeometry(shape, segments);
  g.rotateX(-Math.PI / 2);
  g.translate(x, y, z);
  return g;
}

/** Círculo plano (punto de una matriz, lente de un LED). */
export function flatDisc(r, x, y, z, segments = 20) {
  const g = new THREE.CircleGeometry(r, segments);
  g.rotateX(-Math.PI / 2);
  g.translate(x, y, z);
  return g;
}

// Conectores -------------------------------------------------------------------------------------------

/** Tira de pines macho vertical (plástico abajo, pines hacia arriba) sobre una placa en y. */
export function maleHeaderUp(b, xs, z, y, { pinH = 8.5, plastic = 2.5 } = {}) {
  const x0 = Math.min(...xs) - PITCH / 2;
  const x1 = Math.max(...xs) + PITCH / 2;
  b.box(x1 - x0, plastic, PITCH, M.blackPlastic, (x0 + x1) / 2, y, z);
  for (const x of xs) b.box(0.64, pinH, 0.64, M.gold, x, y, z);
}

/**
 * Tira de pines macho en ángulo recto: el plástico queda acostado junto al borde de la placa (encima o
 * debajo, centrado en la altura de los pines) y los pines salen hacia `dir` hasta `tip`.
 * axis: 'x' (pines a lo largo de X, fila a lo largo de Z) o 'z'. pcbY: cara de la placa donde se sueldan.
 */
export function rightAngleHeader(b, { axis = 'x', dir = 1, along, edge, tip, pinY, pcbY }) {
  const span = along.length * PITCH;
  const mid = (Math.min(...along) + Math.max(...along)) / 2;
  const pc = edge - (dir * PITCH) / 2; // centro del plástico, justo dentro del borde
  const bend = edge - dir * (PITCH + 0.9); // codo hacia la placa
  const h0 = Math.min(bend, tip);
  const h1 = Math.max(bend, tip);
  const v0 = Math.min(pinY, pcbY);
  const vh = Math.abs(pinY - pcbY) + 0.32;
  const put = (w, h, d, material, u, y, v) => (axis === 'x' ? b.box(w, h, d, material, u, y, v) : b.box(d, h, w, material, v, y, u));
  put(PITCH, PITCH, span, M.blackPlastic, pc, pinY - PITCH / 2, mid);
  for (const a of along) {
    put(h1 - h0, 0.64, 0.64, M.gold, (h0 + h1) / 2, pinY - 0.32, a);
    put(0.64, vh, 0.64, M.gold, bend, v0 - (pinY < pcbY ? 0.32 : 0), a);
  }
}

/** Patas cortas que bajan a la protoboard desde el cuerpo de un componente DIP. */
export function dipLegs(b, pins, top) {
  for (const p of pins) b.box(0.5, top, 0.28, M.tin, p.x, 0, p.z);
}

/** Material plástico para cuerpos de displays. */
export const BODY = {
  get face() { return mat('#1d1e21', { roughness: 0.55 }); },
  get sides() { return mat('#2a2b2f', { roughness: 0.6 }); },
  get white() { return mat('#e9e7e0', { roughness: 0.55 }); },
};
